import React, { createContext, useState, useContext, useEffect } from "react";

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within NavigationProvider");
  }
  return context;
};

// Navigation stage definitions
export const STAGES = {
  HOME: "home",
  STAGE_1: "stage_1",
  STAGE_2A: "stage_2a",
  STAGE_2B: "stage_2b",
  STAGE_3A: "stage_3a",
  STAGE_3B: "stage_3b",
  // Destination pages
  PENCILS: "pencils",
  INKS: "inks",
  NOTES: "notes",
  ACTIVITIES: "activities",
  SPECTACLES: "spectacles",
  VAULT: "vault",
};

// Transition messages for each destination
export const TRANSITION_MESSAGES = {
  [STAGES.PENCILS]: "A sharp pencil lies waiting.",
  [STAGES.INKS]: "The stars look like spilled ink.",
  [STAGES.NOTES]: "Pages rustle in the silence.",
  [STAGES.ACTIVITIES]: "The pulse of the day is steady.",
  [STAGES.SPECTACLES]: "Through the lens, everything shifts.",
  [STAGES.VAULT]: "The safe clicks open.",
  [STAGES.HOME]: "The bulb flickers and dies. You are left in the dark.",
};

export const NavigationProvider = ({ children }) => {
  const [currentStage, setCurrentStage] = useState(STAGES.HOME);
  const [navigationHistory, setNavigationHistory] = useState([STAGES.HOME]);
  const [isBlackout, setIsBlackout] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Load navigation state from localStorage
  useEffect(() => {
    const savedStage = localStorage.getItem("current_stage");
    const savedHistory = localStorage.getItem("navigation_history");

    if (savedStage) {
      setCurrentStage(savedStage);
    }
    if (savedHistory) {
      try {
        setNavigationHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Failed to parse navigation history");
      }
    }
  }, []);

  // Save navigation state to localStorage
  useEffect(() => {
    localStorage.setItem("current_stage", currentStage);
    localStorage.setItem(
      "navigation_history",
      JSON.stringify(navigationHistory),
    );
  }, [currentStage, navigationHistory]);

  const navigateTo = (stage, skipTransition = false) => {
    // Check if stage has a transition message
    const hasTransitionMessage = TRANSITION_MESSAGES[stage];

    if (hasTransitionMessage && !skipTransition) {
      // Show transition overlay
      setTransitionMessage(TRANSITION_MESSAGES[stage]);
      setIsTransitioning(true);

      // Navigate after transition completes
      setTimeout(() => {
        if (stage === STAGES.HOME) {
          // Reset to home completely
          setCurrentStage(STAGES.HOME);
          setNavigationHistory([STAGES.HOME]);
        } else {
          setNavigationHistory((prev) => [...prev, stage]);
          setCurrentStage(stage);
        }
        setIsTransitioning(false);
      }, 2400);
    } else {
      // Navigate immediately
      if (stage === STAGES.HOME) {
        setCurrentStage(STAGES.HOME);
        setNavigationHistory([STAGES.HOME]);
      } else {
        setNavigationHistory((prev) => [...prev, stage]);
        setCurrentStage(stage);
      }
    }
  };

  const goBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const previousStage = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setCurrentStage(previousStage);
    }
  };

  const resetToHome = () => {
    setCurrentStage(STAGES.HOME);
    setNavigationHistory([STAGES.HOME]);
  };

  const triggerBlackout = () => {
    setIsBlackout(true);
    setTimeout(() => {
      setIsBlackout(false);
      resetToHome();
    }, 3000);
  };

  const isDestinationPage = () => {
    return [
      STAGES.PENCILS,
      STAGES.INKS,
      STAGES.NOTES,
      STAGES.ACTIVITIES,
      STAGES.SPECTACLES,
      STAGES.VAULT,
    ].includes(currentStage);
  };

  const value = {
    currentStage,
    navigationHistory,
    navigateTo,
    goBack,
    resetToHome,
    triggerBlackout,
    isBlackout,
    isDestinationPage: isDestinationPage(),
    transitionMessage,
    isTransitioning,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
